import { useEffect } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { installSpaLinkInterceptor } from "@/lib/spa-links";
import { pasangPelacakFetch, ProgressBar } from "@/lib/progress";
import { TenantProvider, allowedPaths, useTenant } from "@/lib/tenant";
import ALayout from "@/pages/a/layout";
import LoginPage from "@/pages/login";
import Dashboard from "@/pages/a/dashboard/page";
import Issues from "@/pages/a/issues/page";
import IssueDetail from "@/pages/a/issues/detail-page";
import Articles from "@/pages/a/monitoring/articles/page";
import MediaAnalytics from "@/pages/a/analytics/page";
import Persons from "@/pages/a/analysis/persons/page";
import TopMedia from "@/pages/a/analysis/media/page";
import MediaGeo from "@/pages/a/analysis/geo/page";
import MediaClusters from "@/pages/a/analysis/clusters/page";
import SocialPosts from "@/pages/a/social/posts/page";
import SocialAnalytics from "@/pages/a/social/analytics/page";
import Influencers from "@/pages/a/social/influencers/page";
import SocialGeo from "@/pages/a/social/geo/page";
import SocialClusters from "@/pages/a/social/clusters/page";
import Sync from "@/pages/a/sync/page";
import Keywords from "@/pages/a/settings/keywords/page";
import Liputan from "@/pages/a/liputan/page";
import LiputanDetail from "@/pages/a/liputan/detail-page";

/**
 * Gerbang sesi + entitlement.
 *
 * CATATAN KEAMANAN: ini UX, bukan otorisasi. Penjaga sebenarnya ada di server
 * (middleware + requireFeature + requirePermission di apps/web). Gunanya di sini
 * supaya pengguna tidak mendarat di halaman yang endpoint-nya pasti menolak.
 */
function RequireAccess() {
  const { status, navigation, error } = useTenant();
  const loc = useLocation();

  if (status === "loading") {
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: "100vh" }}>
        <div className="avio-skeleton" style={{ width: 220, height: 12 }} />
      </div>
    );
  }
  if (status === "anon") return <Navigate to="/login" replace />;
  if (status === "error") {
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: "100vh", padding: 24 }}>
        <div className="avio-empty" data-tone="error" style={{ maxWidth: 460 }}>
          <i className="fa-solid fa-triangle-exclamation avio-empty__icon" />
          <div className="avio-empty__title">Konfigurasi tenant tidak dapat dimuat</div>
          <div className="avio-empty__hint">{error}</div>
        </div>
      </div>
    );
  }

  const allowed = allowedPaths(navigation);
  const first = navigation[0]?.items[0]?.path;
  const boleh = [...allowed].some((p) => loc.pathname === p || loc.pathname.startsWith(p + "/"));

  if (!boleh) {
    // Tidak ada satu pun menu aktif: tampilkan alasannya, jangan memantul terus.
    if (!first) {
      return (
        <div style={{ display: "grid", placeItems: "center", minHeight: "100vh", padding: 24 }}>
          <div className="avio-empty" style={{ maxWidth: 460 }}>
            <i className="fa-solid fa-lock avio-empty__icon" />
            <div className="avio-empty__title">Belum ada fitur yang aktif</div>
            <div className="avio-empty__hint">
              Paket langganan tenant ini belum mengaktifkan modul apa pun, atau akun Anda belum
              diberi izin. Hubungi administrator AVIO.
            </div>
          </div>
        </div>
      );
    }
    return <Navigate to={first} replace />;
  }
  return <Outlet />;
}

// Dipasang di tingkat modul, bukan di dalam efek: TenantProvider menembak
// permintaan pertamanya sebelum efek App sempat berjalan, dan permintaan itu
// justru yang paling pantas ditandai sedang dimuat.
pasangPelacakFetch();

export default function App() {
  const navigate = useNavigate();
  useEffect(() => installSpaLinkInterceptor((href) => navigate(href)), [navigate]);

  return (
    <TenantProvider>
      <ProgressBar />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<RequireAccess />}>
          <Route path="/a" element={<ALayout />}>
            <Route index element={<Navigate to="/a/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="issues" element={<Issues />} />
            <Route path="issues/:id" element={<IssueDetail />} />
            <Route path="monitoring/articles" element={<Articles />} />
            <Route path="analytics" element={<MediaAnalytics />} />
            <Route path="analysis/persons" element={<Persons />} />
            <Route path="analysis/media" element={<TopMedia />} />
            <Route path="analysis/geo" element={<MediaGeo />} />
            <Route path="analysis/clusters" element={<MediaClusters />} />
            <Route path="social/posts" element={<SocialPosts />} />
            <Route path="social/analytics" element={<SocialAnalytics />} />
            <Route path="social/influencers" element={<Influencers />} />
            <Route path="social/geo" element={<SocialGeo />} />
            <Route path="social/clusters" element={<SocialClusters />} />
            <Route path="sync" element={<Sync />} />
            <Route path="liputan" element={<Liputan />} />
            <Route path="liputan/:id" element={<LiputanDetail />} />
            <Route path="settings/keywords" element={<Keywords />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/a/dashboard" replace />} />
      </Routes>
    </TenantProvider>
  );
}
